import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Award, Star, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

interface BearFluencerBadgeProps {
  size?: 'small' | 'medium' | 'large';
  showTooltip?: boolean;
  className?: string;
  variant?: 'default' | 'minimal' | 'prominent';
}

export default function BearFluencerBadge({ 
  size = 'medium', 
  showTooltip = true, 
  className,
  variant = 'default'
}: BearFluencerBadgeProps) {
  const sizeClasses = {
    small: 'h-5 w-5 text-xs',
    medium: 'h-6 w-6 text-sm',
    large: 'h-8 w-8 text-base'
  };

  const badgeContent = (
    <Badge 
      className={cn(
        variant === 'prominent' 
          ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 shadow-lg"
          : variant === 'minimal'
          ? "bg-purple-100 text-purple-800 border-purple-300"
          : "bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0",
        "px-2 py-1 flex items-center gap-1 hover:scale-105 transition-transform cursor-pointer",
        className
      )}
      data-testid="badge-bearfluencer"
    >
      <div className="relative flex items-center">
        {variant === 'prominent' && (
          <Sparkles className={cn(sizeClasses[size], "animate-pulse absolute -left-1")} />
        )}
        <Star className={cn(sizeClasses[size], "fill-current")} />
        {variant === 'prominent' && (
          <Sparkles className={cn(sizeClasses[size], "animate-pulse absolute -right-1")} />
        )}
      </div>
      <span className={cn(
        size === 'small' ? 'text-xs font-medium' : 
        size === 'large' ? 'text-base font-bold' : 
        'text-sm font-semibold'
      )}>
        BearFluencer
      </span>
    </Badge>
  );

  if (showTooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {badgeContent}
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-1 p-1">
              <p className="font-semibold flex items-center gap-1">
                <Award className="h-4 w-4 text-purple-600" />
                Verified BearFluencer Partner
              </p>
              <p className="text-xs text-muted-foreground">
                This user is an approved influencer partner with exclusive access to promotional materials and special commission rates.
              </p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return badgeContent;
}